import { useRouter } from "next/router";
import { IEvent, IHost } from "@Interfaces";
import { IFormFieldV2 } from "@Modules/common/Form/FormFieldV2/IFormFieldV2";
import { IDropdownSelector } from "@Modules/common/Form/FormInputs/DropdownSelector/IDropdownSelector";
import {
    ACTION_TYPES,
    API_ACTION_TYPES,
    createAction,
    EXPRESS_INTEREST_MESSAGE_BEHAVIOR,
    INDIA,
    isEmpty,
    isIndia,
    isValidURL,
    SELECTORS,
    useEventDataOfSelectedEvent,
    useProcessing,
} from "@Utils";
import { EVENT_ENUM } from "@Utils/constants/event";
import {
    COUNTRY_CODE_DROPDOWN_OPTIONS as countryCodes,
    ICountryCodeDropdownOption,
    getCountryCodeDropdownOptionFromKey,
} from "utils/location";
import { processCustomQuestions } from "@Utils/customQuestions";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { getAnalyticsInfo } from "repoV2/utils/common/analytics/analyticsInfo";
import { getUtmParams } from "repoV2/utils/common/analytics/utmParams";
import { EMAIL_REGEX } from "repoV2/utils/common/validators/regex.constants";
import { APP_ROUTE_KEYS } from "repoV2/constants/urls&routing/routing";
import { getEmailHasValidTopLevelDomain } from "repoV2/utils/common/validators/validators";
import { setSessionStorageItem } from "repoV2/utils/common/storage/getterAndSetters";
import useToastMessage from "repoV2/components/common/Toast/useToastMessage";
import { postAnalytics } from "repoV2/features/Common/Analytics/Analytics.utils";
import { URL_SEARCH_PARAMS } from "repoV2/features/Common/modules/URLsNRouting/URLsNRouting.constants";
import { ANALYTICS_EVENTS } from "repoV2/features/Common/Analytics/Analytics.constants";
import { SESSION_STORAGE_KEYS } from "@Modules/thankyou/constants/thankyou.contants";
import { constructRoute } from "repoV2/utils/urls&routing/routing";
import { FORM_LOG_TYPES } from "repoV2/features/Listing/Listing.constants";
import { CUSTOM_QUESTION_TYPES_ID } from "repoV2/utils/getInTouchForm/getInTouchForm.constants";
import { IExpressInterestProps } from "../ExpressInterest";
import { FORM_FIELDS, IFormFields } from "./FormDetails";
import {
    getExpressInterestCtaText,
    getExpressInterestTitle,
} from "./expressInterest.helpers";

const useExpressInterest = ({
    hostName,
    disablePayment,
    isSoldOut,
}: IExpressInterestProps) => {
    const { eventData = {} as IEvent.IEventData } =
        useEventDataOfSelectedEvent();
    const dispatch = useDispatch();
    const { startProcessing, stopProcessing } = useProcessing();
    const router = useRouter();
    const { notify } = useToastMessage();

    const {
        register,
        handleSubmit,
        formState: { errors, defaultValues },
        getFieldState,
        getValues,
        setValue,
        trigger,
        reset,
        control: formControls,
        watch,
        setError,
        clearErrors,
    } = useForm<
        IFormFields & {
            // union because CustomQuestions is a resusable compoent, not the part of express interest form
            // we don't to include it in FORM_ENUM
            lead_answers: { [key: string]: string | string[] };
        }
    >({
        defaultValues: {
            country: getCountryCodeDropdownOptionFromKey(),
            lead_answers: {},
            lead_answer_thumbnails: {},
        },
    });

    const [showErrors, setShowErrors] = useState(false);
    const [expressInterestSuccess, setExpressInterestSuccess] = useState(false);

    const hostData = useSelector(SELECTORS.hostData) || ({} as IHost.IHostData);

    const isInternationalCreator = !!hostData.is_international_creator;

    const customQuestions = hostData.leads_q_and_a || [];
    const showCustomQns = !isEmpty(customQuestions);

    const customAnswers = watch("lead_answers");
    const leadAnswerThumbnailsKey =
        FORM_FIELDS.lead_answer_thumbnails as keyof IFormFields;
    const customAnswerThumbnails = watch(
        leadAnswerThumbnailsKey
    ) as IFormFields["lead_answer_thumbnails"];
    const listingExpressInterestCta = getExpressInterestCtaText({
        eventStatus: eventData.status,
        hostData,
    });

    const title = getExpressInterestTitle({ eventData, hostData });

    useEffect(() => {
        if (expressInterestSuccess) {
            reset();

            dispatch(
                createAction(ACTION_TYPES.USER.UPDATE_ANSWERS, {
                    answers: {},
                })
            );
            setExpressInterestSuccess(false);
        }
    }, [expressInterestSuccess]);

    const handleCountryCodeChange = (val: IDropdownSelector.Option | null) => {
        setValue("country", val as ICountryCodeDropdownOption);

        if (getFieldState("phone").isTouched) trigger("phone");
    };

    const getFieldProps = (field?: IFormFieldV2) => {
        if (!field) return undefined;

        const { key: fieldName } = field;

        return {
            ...field,
            ...register(fieldName as keyof IFormFields, {
                validate: value =>
                    field.validate?.(value, {
                        ...getValues(),
                    }),
            }),
            error: !!errors[fieldName as keyof IFormFields],
            showError: !!errors[fieldName as keyof IFormFields],
            errorMessage: errors[fieldName as keyof IFormFields]?.message || "",
        };
    };

    const handleLeadAnswersError = (leadAnswers: {
        [k: string]: string | Array<string>;
    }) => {
        // Error conditions:
        // 1. If the question is mandatory and the answer is empty
        // 2. If the question type is URL, the answer must be a valid URL (if it's a string)
        const hasQuestionaError = customQuestions.some(
            ({ uuid, is_mandatory, type }) => {
                const answer = leadAnswers[uuid];
                return (
                    (is_mandatory && isEmpty(answer)) ||
                    (type === CUSTOM_QUESTION_TYPES_ID.URL &&
                        typeof answer === "string" &&
                        !isValidURL(answer))
                );
            }
        );
        if (hasQuestionaError)
            setError("lead_answers", { type: "validate", message: "Required" });
        else clearErrors("lead_answers");

        return !!hasQuestionaError;
    };

    const setCustomAns = (newAnswers: {
        [k: string]: string | Array<string>;
    }): void => {
        const currentCustomAnswers = getValues("lead_answers");
        const updatedCustomAnserts = { ...currentCustomAnswers, ...newAnswers };
        setValue("lead_answers", updatedCustomAnserts);
        handleLeadAnswersError(updatedCustomAnserts);
    };

    const setCustomAnsThumbnail = (newThumbnails: {
        [k: string]: string;
    }): void => {
        const currentCustomAnswerThumbnails = getValues(
            leadAnswerThumbnailsKey
        ) as IFormFields["lead_answer_thumbnails"];
        const updatedCustomAnswerThumbnails = {
            ...currentCustomAnswerThumbnails,
            ...newThumbnails,
        };
        setValue(leadAnswerThumbnailsKey, updatedCustomAnswerThumbnails);
    };

    const handleExpressInterest = (leadGenFormData: any) => {
        const { lead_answers } = leadGenFormData;

        if (handleLeadAnswersError(lead_answers)) {
            onError();
            return;
        }

        const {
            full_name: fullName,
            email,
            phone_number: phoneNumber,
        } = leadGenFormData;
        const { name: country, dial_code: countryCode } =
            leadGenFormData[FORM_FIELDS.country];
        const message = leadGenFormData[FORM_FIELDS.message] || "";

        startProcessing();

        const utmDetails = getUtmParams();
        /**
         * Fetching user IP details required for sending in Payload for analytics
         */
        const analyticsInfo = getAnalyticsInfo();
        const hostMessagePayload = {
            ...leadGenFormData,
            message,
            country,
            country_code: countryCode,
            ...(!isEmpty(customQuestions) && {
                lead_answers: processCustomQuestions(
                    customQuestions,
                    customAnswers,
                    customAnswerThumbnails
                ),
            }),
            listing_id: eventData.id,
            log_type: FORM_LOG_TYPES.EXPRESSED_INTEREST,
            sub_domain: hostName,
            utm_info: utmDetails,
            analytics_info: analyticsInfo,
            ...(router.query[URL_SEARCH_PARAMS.DYNAMIC_LINK] && {
                dynamic_link_id: router.query[URL_SEARCH_PARAMS.DYNAMIC_LINK],
            }),
        };

        // removing lead_answer_thumbnails that came from leadGenFormData
        delete hostMessagePayload.lead_answer_thumbnails;

        postAnalytics(ANALYTICS_EVENTS.LEAD, {
            sub_domain: hostName,
            event_source_url: window.location.href,
            content_name: eventData.title,
            content_category: EVENT_ENUM[eventData.type].TITLE,
            currency: eventData?.currency_name,
            value: eventData?.price_per_head,
            user_data: {
                em: email,
                name: fullName,
                ph: `${countryCode}${phoneNumber}`,
            },
        });

        dispatch(
            createAction(ACTION_TYPES.UTILS.API_CALL, {
                apiActionType: API_ACTION_TYPES.SEND_HOST_MESSAGE,
                payload: hostMessagePayload,
                successCallback: () => {
                    if (!isSoldOut)
                        notify("Your message has been successfully sent.", {
                            type: "success",
                        });
                    // @dev
                    // If sold out opt-in Offering then save the data for the express Interest
                    // form as it will be required to display on optin-thankyou page to show
                    // leads details as its not recieved from backend and has very low TTL so used session storage
                    // and will not be used elsewhere so not stored in redux.
                    else if (isSoldOut) {
                        setSessionStorageItem(
                            SESSION_STORAGE_KEYS.EXPRESS_INTEREST_FORM_DATA,
                            JSON.stringify({
                                ...hostMessagePayload,
                                unprocessed_lead_answers: {
                                    customQuestions,
                                    customAnswers,
                                },
                            })
                        );
                        router.push(
                            constructRoute({
                                appRouteKey: APP_ROUTE_KEYS.THANKYOU_OPTIN,
                                args: {
                                    slug: eventData.uuid,
                                },
                            })
                        );
                    }

                    // If there is a post interest URL, open that
                    if (eventData.metadata?.post_interest_url) {
                        window.open(
                            eventData.metadata?.post_interest_url,
                            "_blank"
                        );
                    }

                    setExpressInterestSuccess(true);
                    setShowErrors(false);
                },
                errorCallback: payload => {
                    notify(
                        payload?.response?.message ||
                            "An error occurred in sending the message. Please try again.",
                        { type: "error" }
                    );
                },
                finallyCallback: () => {
                    stopProcessing();
                },
            })
        );
    };

    const hasError = (field: IFormFieldV2) =>
        !isEmpty(getFieldState(field.key as keyof IFormFields).error);

    const getFieldLabel = (defaultVal: string, override?: string) =>
        override || defaultVal;

    const getFieldPlaceholder = (defaultVal: string, override?: string) =>
        `Enter ${getFieldLabel(defaultVal, override)}`;

    // admin panel override for express interest message behaviour
    const isExpressInterestMessageOptional =
        hostData?.express_interest_message_behaviour ===
        EXPRESS_INTEREST_MESSAGE_BEHAVIOR.OPTIONAL;
    const isExpressInterestMessageHidden =
        hostData?.express_interest_message_behaviour ===
        EXPRESS_INTEREST_MESSAGE_BEHAVIOR.HIDDEN;

    const FORM_ENUM: { [key: string]: IFormFieldV2 } = {
        [FORM_FIELDS.name]: {
            id: FORM_FIELDS.name, // Internal ID for react to identify the field with
            label: getFieldLabel(
                "Full Name",
                hostData?.custom_get_in_touch_form_field_name
            ), // Label to show above the field
            placeholder: getFieldPlaceholder(
                "Full Name",
                hostData?.custom_get_in_touch_form_field_name
            ),
            key: FORM_FIELDS.name, // Key to send in the API data
            inputType: "text", // HTML input type
            params: {}, // Additional Params for the HTML field
            validate: (data: string) =>
                data.trim().length > 0 ? undefined : "Please enter a name", // Form field validator
            mandatory: () => true,
        },
        [FORM_FIELDS.email]: {
            id: FORM_FIELDS.email,
            label: getFieldLabel(
                "Email",
                hostData?.custom_get_in_touch_form_field_email
            ), // Label to show above the field
            placeholder: getFieldPlaceholder(
                "Email",
                hostData?.custom_get_in_touch_form_field_email
            ),
            key: FORM_FIELDS.email,
            inputType: "email",
            params: {},
            validate: (data: string = "") => {
                const disableCheckEmailTopLevelDomain =
                    hostData?.disable_booking_email_whitelisting || false;
                if (!data.trim().length) return "Please enter an email";
                if (
                    !EMAIL_REGEX.test(data) ||
                    (!disableCheckEmailTopLevelDomain &&
                        !getEmailHasValidTopLevelDomain(data))
                )
                    return "Invalid email format";
                return undefined;
            },
            mandatory: () => true,
        },
        [FORM_FIELDS.phone]: {
            preField: {
                inputType: "select",
                key: FORM_FIELDS.country,
                params: {
                    options: countryCodes,
                    control: formControls,
                    onChange: handleCountryCodeChange,
                },
                defaultValue: defaultValues?.country,
            },
            id: FORM_FIELDS.phone,
            label: getFieldLabel(
                "Mobile Number",
                hostData?.custom_get_in_touch_form_field_phone
            ), // Label to show above the field
            placeholder: getFieldPlaceholder(
                "Mobile Number",
                hostData?.custom_get_in_touch_form_field_phone
            ),
            key: FORM_FIELDS.phone,
            inputType: "tel",
            params: {
                maxLength: 15,
            },
            validate: (data: string = "", formValues) => {
                const {
                    country: { name },
                } = formValues || {};

                const isInIndia = isIndia(name || INDIA);

                if (isInternationalCreator && !data.trim().length)
                    return undefined;
                if (!isInternationalCreator && !data.trim().length)
                    return "Please enter a phone number";
                if (!/^\d+$/.test(data))
                    return "Phone number can only have digits";
                if (isInIndia) {
                    if (data.length !== 10) {
                        return "Phone number can only be 10 digits long";
                    }
                } else {
                    if (data.length < 4) return "Phone number too short";
                    if (data.length > 16) return "Phone number too long";
                }
                return undefined;
            },
            mandatory: () => !isInternationalCreator,
        },
        [FORM_FIELDS.message]: {
            id: FORM_FIELDS.message,
            label: getFieldLabel(
                "Message",
                hostData?.custom_get_in_touch_form_field_message
            ), // Label to show above the field
            placeholder: getFieldPlaceholder(
                "Message",
                hostData?.custom_get_in_touch_form_field_message
            ),
            key: FORM_FIELDS.message,
            inputType: "textarea",
            params: {
                rows: 3,
            },
            validate: (data: string) =>
                data?.trim().length > 0 ||
                isExpressInterestMessageOptional ||
                isExpressInterestMessageHidden
                    ? undefined
                    : "Please enter a message", // Form field validator
            mandatory: () =>
                !isExpressInterestMessageOptional &&
                !isExpressInterestMessageHidden,
            hidden: () => isExpressInterestMessageHidden,
        },
    };

    const onError = () => {
        setShowErrors(true);
        notify("Please fill the form with valid data", {
            type: "error",
        });
    };

    const onSubmit = handleSubmit(handleExpressInterest, onError);

    return {
        hideComponent: !disablePayment,
        title,
        customQuestions,
        customAnswers,
        setCustomAns,
        customAnswerThumbnails,
        setCustomAnsThumbnail,
        showCustomQns,
        FORM_ENUM,
        getFieldProps,
        hasError,
        onSubmit,
        showErrors,
        listingExpressInterestCta,
    };
};

export default useExpressInterest;
